import { Link, useNavigate, useParams } from 'react-router-dom';
import { useState } from 'react';
import { api } from '../../lib/supabase';
import { useApi } from '../../lib/use-api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  ArrowLeft, ScanLine, CheckCircle2, Loader2,
  CreditCard, ChevronRight, Building2, AlertTriangle,
} from 'lucide-react';
import { Card } from '../../components/ui/card';
import { toast } from 'sonner';
import { formatFee } from '../../lib/currency';
import { useUnits, formatEventDistance } from '../../lib/units';

/**
 * MobileJoinEvent — multi-step join flow per Phase D spec:
 *   1. bib           (only when bibMode != 'none')
 *   2. share-details (NEW) — "Share your name/email/phone with the organizer?"
 *   3. confirm        — final confirmation summary
 *   4. payment        (only when event.isPaid) — bank details + "I've paid" gate
 *   5. done           — success state
 *
 * Registration deadline is enforced: if `event.joinDeadline` has passed we
 * surface a clear blocking message instead of letting the user proceed.
 */
export default function MobileJoinEvent() {
  const { id } = useParams();
  const nav = useNavigate();
  const { units } = useUnits();
  const { data: event, loading } = useApi(api.getEvent, [id]);

  // Build the natural step list given the event's settings. We drop steps
  // the event doesn't need so the user never sees an irrelevant screen.
  const [step, setStep] = useState('start'); // 'start' decides which step is first
  const [bib, setBib] = useState('');
  const [shareDetails, setShareDetails] = useState(false); // must be checked before continuing
  const [paid, setPaid] = useState(false);
  const [busy, setBusy] = useState(false);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  if (!event) return null;

  // ----- deadline check -----
  const deadlineTs = event.joinDeadline ? new Date(event.joinDeadline).getTime() : new Date(event.date).getTime();
  const deadlinePassed = Date.now() > deadlineTs;

  // Decide the *first* step on mount.
  if (step === 'start') {
    if (deadlinePassed) setStep('deadline');
    else if (event.bibMode !== 'none') setStep('bib');
    else setStep('share');
  }

  function afterBib() { setStep('share'); }
  function afterShare() { setStep('confirm'); }
  function afterConfirm() {
    if (event.isPaid) setStep('payment');
    else handleFinalise();
  }

  async function handleFinalise() {
    setBusy(true);
    try {
      await api.joinEvent(event.id, event.bibMode === 'none' ? null : bib);
      setStep('done');
    } catch (err) {
      toast.error(err.message || 'Could not join event');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="px-4 pt-4 pb-6 space-y-5" data-testid="join-event">
      <button onClick={() => nav(-1)} className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="join-back">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div>
        <p className="rivl-label">Joining</p>
        <h1 className="font-display text-2xl font-bold tracking-tight mt-1">{event.name}</h1>
      </div>

      {step === 'deadline' && (
        <Card className="rivl-card p-6 text-center space-y-3" data-testid="join-step-deadline">
          <div className="mx-auto h-14 w-14 rounded-md bg-destructive/10 text-destructive flex items-center justify-center">
            <AlertTriangle className="h-7 w-7" />
          </div>
          <h2 className="font-display text-xl font-bold">Registration closed</h2>
          <p className="text-sm text-muted-foreground">
            The deadline to join this event has passed.
          </p>
          <Button asChild variant="outline" className="h-11 rounded-md">
            <Link to={`/events/${event.id}`}>Back to event</Link>
          </Button>
        </Card>
      )}

      {step === 'bib' && (
        <Card className="rivl-card p-4 space-y-4" data-testid="join-step-bib">
          <div>
            <h2 className="font-display text-lg font-semibold">Link your bib</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {event.bibMode === 'self-assigned'
                ? 'Choose any 4-digit number for your bib.'
                : 'Enter the bib number assigned to you.'}
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="bib">Bib number</Label>
            <div className="flex gap-2">
              <Input
                id="bib"
                data-testid="bib-input"
                value={bib}
                onChange={(e) => setBib(e.target.value.replace(/\D/g, '').slice(0, 5))}
                placeholder="0000"
                inputMode="numeric"
                className="h-11 rounded-md font-mono text-lg tracking-widest"
              />
              <Button variant="outline" className="h-11 rounded-md" data-testid="bib-scan-qr" aria-label="Scan QR">
                <ScanLine className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <Button
            data-testid="bib-confirm-btn"
            disabled={bib.length < 3}
            onClick={afterBib}
            className="w-full h-11 rounded-md"
          >
            Continue <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </Card>
      )}

      {step === 'share' && (
        <Card className="rivl-card p-5 space-y-4" data-testid="join-step-share">
          <h2 className="font-display text-lg font-semibold">Share your details with the organizer</h2>
          <p className="text-sm text-muted-foreground">
            The organizer of <span className="font-medium text-foreground">{event.name}</span> needs
            your name, email, and (if set) phone number for race-day communications
            (start delays, results, medal pickup, etc.).
          </p>
          <label className="flex items-start gap-3 text-sm cursor-pointer" data-testid="share-details-label">
            <input
              type="checkbox"
              checked={shareDetails}
              onChange={(e) => setShareDetails(e.target.checked)}
              className="mt-0.5 h-4 w-4 flex-shrink-0"
              data-testid="share-details-checkbox"
            />
            <span>I agree to share my details for this race.</span>
          </label>
          <Button
            type="button"
            onClick={afterShare}
            disabled={!shareDetails}
            data-testid="share-details-continue"
            className="w-full h-11 rounded-md"
          >
            Continue <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </Card>
      )}

      {step === 'confirm' && (
        <Card className="rivl-card p-5 space-y-4" data-testid="join-step-confirm">
          <h2 className="font-display text-lg font-semibold">Confirm participation</h2>
          <div className="space-y-2 text-sm">
            <Row k="Event" v={event.name} />
            <Row k="Distance" v={formatEventDistance(event.distanceKm, units)} />
            {event.bibMode && event.bibMode !== 'none' && <Row k="Bib" v={bib || '—'} />}
            <Row k="Fee" v={event.isPaid ? formatFee(event.fee, event.currency) : 'Free'} />
            <Row k="Share details" v="Yes" />
          </div>
          <Button
            onClick={afterConfirm}
            disabled={busy}
            className="w-full h-11 rounded-md"
            data-testid="confirm-join-btn"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (event.isPaid ? 'Continue to payment' : 'Confirm join')}
            {!busy && <ChevronRight className="h-4 w-4 ml-1" />}
          </Button>
        </Card>
      )}

      {step === 'payment' && (
        <Card className="rivl-card p-5 space-y-4 border-primary/40" data-testid="join-step-payment">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-md bg-primary text-primary-foreground flex items-center justify-center">
              <CreditCard className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <h2 className="font-display text-lg font-semibold">Make payment</h2>
              <p className="text-xs text-muted-foreground">
                Transfer the fee, then mark as paid below.
              </p>
            </div>
          </div>

          <div className="rivl-card p-4 space-y-3 bg-secondary/20" data-testid="payment-bank-details">
            <Row k="Amount" v={formatFee(event.fee, event.currency)} bold />
            <Row k="Bank" v={event.bankName || '—'} icon={Building2} />
            <Row k="Account number" v={event.accountNumber || '—'} mono />
            <Row k="Account name" v={event.accountName || '—'} />
          </div>

          {(!event.bankName || !event.accountNumber) && (
            <p className="text-[11px] text-destructive">
              Payment details missing — contact the organizer directly.
            </p>
          )}

          <label className="flex items-start gap-3 text-sm cursor-pointer" data-testid="payment-confirm-label">
            <input
              type="checkbox"
              checked={paid}
              onChange={(e) => setPaid(e.target.checked)}
              className="mt-0.5 h-4 w-4"
              data-testid="payment-confirm-checkbox"
            />
            <span>I have made the transfer of <span className="font-semibold">{formatFee(event.fee, event.currency)}</span>.</span>
          </label>

          <Button
            onClick={handleFinalise}
            disabled={!paid || busy}
            className="w-full h-11 rounded-md"
            data-testid="payment-finalise-btn"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Join event"}
          </Button>
        </Card>
      )}

      {step === 'done' && (
        <Card className="rivl-card p-6 text-center space-y-3" data-testid="join-step-done">
          <div className="mx-auto h-14 w-14 rounded-md bg-success/10 text-success flex items-center justify-center">
            <CheckCircle2 className="h-8 w-8" />
          </div>
          <h2 className="font-display text-xl font-bold">You're in.</h2>
          <p className="text-sm text-muted-foreground">
            Your participation is confirmed. {event.isPaid && 'The organizer will verify your transfer shortly.'}
          </p>
          <p className="text-[11px] text-muted-foreground">
            Check-in opens on event day: <span className="font-medium">{new Date(event.date).toLocaleString()}</span>
          </p>
          <div className="grid grid-cols-2 gap-3 pt-2">
            <Button asChild variant="outline" className="h-11 rounded-md">
              <Link to="/" data-testid="join-done-home">Home</Link>
            </Button>
            <Button asChild className="h-11 rounded-md">
              <Link to={`/events/${event.id}`} data-testid="join-done-event">View event</Link>
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

function Row({ k, v, bold, mono, icon: Icon }) {
  return (
    <div className="flex justify-between items-center border-b border-border last:border-0 pb-2 gap-3">
      <span className="text-muted-foreground flex items-center gap-1.5">
        {Icon && <Icon className="h-3.5 w-3.5" />} {k}
      </span>
      <span className={`text-right max-w-[60%] truncate ${bold ? 'font-bold text-base' : 'font-medium'} ${mono ? 'font-mono tracking-wider' : ''}`}>
        {v}
      </span>
    </div>
  );
}
